import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type AdminIntegrationVisibilityRow, adminClient } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDate } from '@/lib/format';
import { visibilitySummary } from '@/lib/integrationVisibility';

import {
  type AskableIntegration,
  type Delegate,
  type Grant,
  type GrantPage,
  type Group,
  PermissionsError,
  endOfDay,
  errorText,
  groupOf,
  levelObject,
  permissionsApi,
  viewersSummary,
} from './permissionsApi';
import { usePersonNames } from './names';
import { BackLink, Problem, givenBy, untilText, whoOf } from './shared';

/** `askable` is missing for an integration with no access levels. */
type Row = { id: string; title: string; askable?: AskableIntegration; visibility?: AdminIntegrationVisibilityRow['visibility'] };

/** "1 delegate", "3 delegates", or "Delegates" when the service does not list them. */
export const delegateCount = (n: number | undefined) => (n === undefined ? 'Delegates' : `${n} delegate${n === 1 ? '' : 's'}`);

/** Every integration of the admin catalog and of the service's manifests, each with its levels when it has any. */
export function joinRows(askable: AskableIntegration[], visibility: AdminIntegrationVisibilityRow[]): Row[] {
  const levels = new Map(askable.map((integration) => [integration.id, integration]));
  const catalog = new Map(visibility.map((row) => [row.integrationId, row]));
  const ids = [...new Set([...catalog.keys(), ...levels.keys()])];
  return ids
    .map((id) => ({ id, title: catalog.get(id)?.title ?? id, askable: levels.get(id), visibility: catalog.get(id)?.visibility }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/** `integration#user` rows of each integration. A missing key means the read failed: that row is never treated as "no rule". */
type Viewers = Record<string, Grant[]>;

/** The rows of each integration, a few calls at a time. A failed read leaves its integration out. */
async function loadViewers(ids: string[], into: (id: string, rows: Grant[]) => void, current: () => boolean) {
  for (let i = 0; i < ids.length && current(); i += 6) {
    await Promise.all(
      ids.slice(i, i + 6).map(async (id) => {
        try {
          const rows = await permissionsApi.integrationViewers(id);
          if (current()) into(id, rows); // a newer load replaced this one: its answer is stale
        } catch {
          // No answer for this integration: the list shows a dash and Open to everyone skips it.
        }
      }),
    );
  }
}

/** The integrations whose `integration#user` rows were read and are empty: the ones "Open to everyone" writes to. */
export const withoutRule = (rows: Row[], viewers: Viewers) => rows.filter((row) => viewers[row.id]?.length === 0);

/** Holder counts, a few calls at a time. A count that fails stays out of the map. */
async function loadCounts(rows: Row[], into: (key: string, count: number) => void) {
  const jobs = rows.flatMap((row) => (row.askable?.levels ?? []).map((level) => [row.id, level.id] as const));
  for (let i = 0; i < jobs.length; i += 6) {
    await Promise.all(
      jobs.slice(i, i + 6).map(async ([integration, level]) => {
        try {
          into(`${integration}.${level}`, await permissionsApi.holderCount(integration, level));
        } catch {
          // No count for this level: the list shows a dash.
        }
      }),
    );
  }
}

const COLUMNS = [
  { label: 'Integration' },
  { label: 'Organizations', className: 'hidden md:table-cell' },
  { label: 'Who can see it' },
  { label: 'Levels and holders' },
  { label: 'Decides requests', className: 'text-right' },
];

export function IntegrationsTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const [rows, setRows] = useState<Row[]>();
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState('');
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [viewers, setViewers] = useState<Viewers>({});
  const [confirming, setConfirming] = useState(false);
  const loadRun = useRef(0);

  const load = useCallback(async () => {
    setError(undefined);
    try {
      const [askable, visibility] = await Promise.all([
        permissionsApi.askable(),
        adminClient.listIntegrationVisibility().then((out) => out.items).catch(() => [] as AdminIntegrationVisibilityRow[]),
      ]);
      const joined = joinRows(askable, visibility);
      setRows(joined);
      void loadCounts(joined, (key, count) => setCounts((now) => ({ ...now, [key]: count })));
      setViewers({});
      const run = ++loadRun.current;
      void loadViewers(joined.map((row) => row.id), (id, list) => setViewers((now) => ({ ...now, [id]: list })), () => run === loadRun.current);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (rows ?? []).filter((row) => !needle || row.title.toLowerCase().includes(needle) || row.id.includes(needle));
  }, [rows, query]);

  const selected = open ? rows?.find((row) => row.id === open) : undefined;
  if (open && selected) return <IntegrationDetail row={selected} onBack={() => onOpen()} onChanged={() => void load()} />;

  const toOpen = withoutRule(rows ?? [], viewers);
  return (
    <>
    {confirming ? <OpenToEveryone rows={toOpen} onClose={() => setConfirming(false)} onDone={() => { setConfirming(false); void load(); }} /> : null}
    <DataTable
      toolbar={
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={query} onChange={setQuery} placeholder="Search name or integration id" label="Search integrations" />
          <Button type="button" size="sm" variant="outline" disabled={toOpen.length === 0} onClick={() => setConfirming(true)}>
            Open to everyone ({toOpen.length})
          </Button>
        </div>
      }
    >
      <DataTableHead columns={COLUMNS} />
      {error ? (
        <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
      ) : !rows ? (
        <DataTableMessage colSpan={COLUMNS.length} loading>
          Loading integrations
        </DataTableMessage>
      ) : shown.length === 0 ? (
        <DataTableMessage colSpan={COLUMNS.length}>{open ? 'This integration is not in the service manifests.' : 'No integration matches.'}</DataTableMessage>
      ) : (
        <tbody>
          {shown.map((row) => (
            <DataTableRow key={row.id} to={`?tab=integrations&open=${encodeURIComponent(row.id)}`}>
              <DataTableCell>
                <button type="button" className="text-left font-medium text-foreground hover:underline" onClick={() => onOpen(row.id)}>
                  {row.title}
                </button>
                <div className="text-xs text-muted-foreground">{row.id}</div>
              </DataTableCell>
              <DataTableCell className="hidden md:table-cell">{row.visibility ? <StatusPill tone={visibilitySummary({ visibility: row.visibility }).tone}>{visibilitySummary({ visibility: row.visibility }).label}</StatusPill> : <span className="text-muted-foreground">n/a</span>}</DataTableCell>
              <DataTableCell>{viewers[row.id] ? viewersSummary(viewers[row.id]) : <span className="text-muted-foreground">–</span>}</DataTableCell>
              <DataTableCell>
                {row.askable?.levels.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {row.askable.levels.map((level) => (
                      <StatusPill key={level.id} tone={level.writes ? 'warning' : 'neutral'}>
                        {level.id} {counts[`${row.id}.${level.id}`] ?? '–'}
                      </StatusPill>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">No access levels</span>
                )}
              </DataTableCell>
              <DataTableCell className="text-right text-xs text-muted-foreground">
                {!row.askable ? '–' : row.askable.granter.kind === 'delegates' ? delegateCount(row.askable.granter.delegates?.length) : 'Admins'}
              </DataTableCell>
            </DataTableRow>
          ))}
        </tbody>
      )}
    </DataTable>
    </>
  );
}

/** One admin action: write `user:*` on each integration that has no `integration#user` row. It lists them and asks first. */
function OpenToEveryone({ rows, onClose, onDone }: { rows: Row[]; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const run = async () => {
    setBusy(true);
    const failed: string[] = [];
    for (const row of rows) {
      try {
        // Read again just before the write: a rule that another admin wrote since the list loaded must not get a user:* beside it.
        if ((await permissionsApi.integrationViewers(row.id)).length > 0) continue;
        await permissionsApi.allowToSee(row.id, { kind: 'everyone' }, 'Open to everyone, before visibility is limited');
      } catch (failure) {
        // A row that already exists (409) means another admin wrote it first: not a failure.
        if (!(failure instanceof PermissionsError && failure.status === 409)) failed.push(`${row.title}: ${errorText(failure)}`);
      }
    }
    setBusy(false);
    if (failed.length) setProblems(failed);
    else onDone();
  };
  return (
    <AdminSection title="Open to everyone" description="These integrations have no rule yet. This gives each one to every person, so nothing changes for anybody.">
      <ul className="mb-3 max-h-48 list-disc space-y-0.5 overflow-auto pl-5 text-sm">
        {rows.map((row) => (
          <li key={row.id}>
            {row.title} <span className="text-xs text-muted-foreground">{row.id}</span>
          </li>
        ))}
      </ul>
      {problems.map((text) => (
        <div key={text} className="mb-2">
          <Problem message={text} />
        </div>
      ))}
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={busy || rows.length === 0} onClick={() => void run()}>
          {busy ? 'Writing…' : `Open ${rows.length} to everyone`}
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </AdminSection>
  );
}

function IntegrationDetail({ row, onBack, onChanged }: { row: Row; onBack: () => void; onChanged: () => void }) {
  const { askable } = row;
  const [version, setVersion] = useState(0);
  const refresh = () => setVersion((n) => n + 1);
  return (
    <div className="space-y-6">
      <div>
        <BackLink label="All integrations" onClick={onBack} />
        <h2 className="text-lg font-semibold">{row.title}</h2>
        <p className="text-xs text-muted-foreground">{row.id}</p>
      </div>
      <WhoCanSee key={`v-${version}`} integration={row.id} onChanged={() => { refresh(); onChanged(); }} />
      {askable?.levels.length ? null : <p className="text-sm text-muted-foreground">No access levels.</p>}
      {askable?.levels.map((level) => (
        <LevelHolders key={`${level.id}-${version}`} integration={askable.id} level={level.id} writes={level.writes} onChanged={refresh} />
      ))}
      {askable ? <Delegates key={`d-${version}`} integration={askable.id} /> : null}
      {askable?.levels.length ? <GiveForm integration={askable} onGiven={refresh} /> : null}
    </div>
  );
}

/** The service refuses a group that its owner could not hold the integration for: say what to do, not the raw refusal. */
export const GROUP_OWNER_TEXT = 'The service refused this group. A group can hold an integration only while the group owner can see it. Add the group while Everyone can still see the integration, then end Everyone.';

/** `integration:<i>#user`: Everyone, groups and people, with Remove and Add. The service decides every write. */
function WhoCanSee({ integration, onChanged }: { integration: string; onChanged: () => void }) {
  const [rows, setRows] = useState<Grant[]>();
  const [groups, setGroups] = useState<Group[]>([]);
  const [error, setError] = useState<string>();
  const [kind, setKind] = useState<'everyone' | 'group' | 'person'>('group');
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    try {
      setRows(await permissionsApi.integrationViewers(integration));
      setError(undefined);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [integration]);
  useEffect(() => {
    void load();
    permissionsApi.groups().then(setGroups).catch(() => undefined); // no list: the group field takes no choice
  }, [load]);
  const act = async (job: () => Promise<void>, group = false) => {
    setBusy(true);
    try {
      await job();
      setValue('');
      setError(undefined);
      onChanged();
      await load();
    } catch (failure) {
      setError(group && failure instanceof PermissionsError && failure.status === 403 ? GROUP_OWNER_TEXT : errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  const who = kind === 'everyone' ? { kind } as const : kind === 'group' ? { kind, name: value } as const : { kind, email: value.trim() } as const;
  const ready = !busy && (kind === 'everyone' || value.trim() !== '');
  const everyoneRows = (rows ?? []).filter((g) => g.subject === 'user:*');
  // The service lets a group hold only what its owner holds. The group is added while Everyone still holds the integration, and only then is Everyone ended.
  const restricting = kind === 'group' && everyoneRows.length > 0;
  const add = () =>
    act(async () => {
      await permissionsApi.allowToSee(integration, who, 'Set in Studio Admin');
      if (restricting) for (const row of everyoneRows) await permissionsApi.endGrant(row.id);
    }, kind === 'group');
  return (
    <AdminSection title="Who can see it" description={rows ? viewersSummary(rows) : undefined} bodyClassName="p-0">
      {error ? <div className="p-4"><Problem message={error} onRetry={() => void load()} /></div> : null}
      {rows?.length === 0 && !error ? <p className="p-5 text-sm text-muted-foreground">Nobody yet. Nobody sees this integration when Studio checks visibility.</p> : null}
      {rows?.length ? (
        <ul className="divide-y divide-border">
          {rows.map((g) => (
            <li key={g.id} className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm">
              <span>
                {groupOf(g.subject) ? `Group ${groupOf(g.subject)}` : whoOf(g)} <span className="text-xs text-muted-foreground">{givenBy(g)}</span>
              </span>
              <Button type="button" size="sm" variant="outline" disabled={busy} aria-label={`Stop showing to ${groupOf(g.subject) ?? whoOf(g)}`} onClick={() => void act(() => permissionsApi.endGrant(g.id))}>
                Remove
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
      <form
        className="flex flex-wrap items-end gap-2 border-t border-border p-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void add();
        }}
      >
        <select aria-label="Who to show it to" value={kind} onChange={(e) => { setKind(e.target.value as typeof kind); setValue(''); }} className="flex h-10 rounded-md border border-input bg-background px-3 text-sm">
          <option value="group">A group</option>
          <option value="person">A person</option>
          <option value="everyone">Everyone</option>
        </select>
        {kind === 'group' ? (
          <select aria-label="Group" value={value} onChange={(e) => setValue(e.target.value)} className="flex h-10 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">Choose a group</option>
            {groups.map((group) => (
              <option key={group.name} value={group.name}>
                {group.name}
              </option>
            ))}
          </select>
        ) : null}
        {kind === 'person' ? <Input type="email" value={value} onChange={(e) => setValue(e.target.value)} placeholder="name@rapidsos.com" aria-label="Person to show it to" className="max-w-xs" /> : null}
        <Button type="submit" size="sm" disabled={!ready}>
          {restricting ? 'Restrict to this group' : 'Add'}
        </Button>
        {restricting ? <span className="text-xs text-muted-foreground">Adds the group first, then ends Everyone.</span> : null}
      </form>
    </AdminSection>
  );
}

function LevelHolders({ integration, level, writes, onChanged }: { integration: string; level: string; writes: boolean; onChanged: () => void }) {
  const [page, setPage] = useState<GrantPage>();
  const [grants, setGrants] = useState<Grant[]>([]);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState<string>();
  const object = levelObject(integration, level);

  const load = useCallback(
    async (after?: string | null) => {
      try {
        const next = await permissionsApi.grants('object', object, { after });
        setPage(next);
        setGrants((now) => (after ? [...now, ...next.grants] : next.grants));
        setError(undefined);
      } catch (failure) {
        setError(errorText(failure));
      }
    },
    [object],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const end = async (grant: Grant) => {
    setBusy(grant.id);
    try {
      await permissionsApi.endGrant(grant.id);
      onChanged();
    } catch (failure) {
      setError(errorText(failure));
      setBusy(undefined);
    }
  };

  return (
    <AdminSection title={`${level}${writes ? ' (can change data)' : ''}`} description={page ? `${page.total} holder${page.total === 1 ? '' : 's'}` : undefined} bodyClassName="p-0">
      {error ? <div className="p-4"><Problem message={error} onRetry={() => void load()} /></div> : null}
      {page && grants.length === 0 && !error ? <p className="p-5 text-sm text-muted-foreground">Nobody holds this level.</p> : null}
      {grants.length > 0 ? (
        <table className="w-full text-left text-sm">
          <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
            <tr>
              {['Person', 'Given by', 'Until', 'Reason', ''].map((label) => (
                <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grants.map((grant) => (
              <tr key={grant.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2.5">{whoOf(grant)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{givenBy(grant)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{untilText(grant.ends_at)}</td>
                <td className="px-4 py-2.5 text-muted-foreground">{grant.reason || '–'}</td>
                <td className="px-4 py-2.5 text-right">
                  <Button type="button" size="sm" variant="outline" disabled={busy === grant.id} onClick={() => void end(grant)} aria-label={`End ${whoOf(grant)} ${level}`}>
                    End
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      {page?.next ? (
        <div className="border-t border-border p-3">
          <Button type="button" size="sm" variant="outline" onClick={() => void load(page.next)}>
            Show more ({grants.length} of {page.total})
          </Button>
        </div>
      ) : null}
    </AdminSection>
  );
}

function Delegates({ integration }: { integration: string }) {
  const nameOf = usePersonNames();
  const [list, setList] = useState<Delegate[]>();
  const [error, setError] = useState<string>();
  const [email, setEmail] = useState('');
  const load = useCallback(async () => {
    try {
      setList(await permissionsApi.delegates(integration));
      setError(undefined);
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [integration]);
  useEffect(() => {
    void load();
  }, [load]);
  const act = async (job: () => Promise<void>) => {
    try {
      await job();
      setEmail('');
      await load();
    } catch (failure) {
      setError(errorText(failure));
    }
  };
  return (
    <AdminSection title="Delegates" description="A delegate decides the requests for this integration. With none, the admins decide.">
      {error ? <Problem message={error} onRetry={() => void load()} /> : null}
      {list?.length === 0 ? <p className="mb-3 text-sm text-muted-foreground">No delegates. The admins decide.</p> : null}
      <ul className="mb-3 space-y-1.5">
        {list?.map((d) => (
          <li key={d.person} className="flex items-center justify-between gap-3 text-sm">
            <span>
              {nameOf(d.person)} <span className="text-xs text-muted-foreground">added {formatDate(d.added_at)}</span>
            </span>
            <Button type="button" size="sm" variant="outline" onClick={() => void act(() => permissionsApi.removeDelegate(integration, d.person))} aria-label={`Remove delegate ${nameOf(d.person)}`}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) void act(() => permissionsApi.addDelegate(integration, email.trim()));
        }}
      >
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email of the new delegate" aria-label="New delegate email" className="max-w-xs" />
        <Button type="submit" size="sm" disabled={!email.trim()}>
          Add delegate
        </Button>
      </form>
    </AdminSection>
  );
}

function GiveForm({ integration, onGiven }: { integration: AskableIntegration; onGiven: () => void }) {
  const [person, setPerson] = useState('');
  const [level, setLevel] = useState(integration.levels[0]?.id ?? '');
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const ready = person.trim() !== '' && level !== '' && reason.trim() !== '' && !busy;
  const submit = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await permissionsApi.give({ person: person.trim(), integration: integration.id, level, endsAt: endOfDay(until), reason: reason.trim() });
      setPerson('');
      setReason('');
      setUntil('');
      onGiven();
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AdminSection title="Give access" description="Writes the level and its operation, the same pair an approved request writes.">
      <form
        className="grid gap-3 md:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) void submit();
        }}
      >
        <label className="text-xs text-muted-foreground">
          Person
          <Input type="email" value={person} onChange={(e) => setPerson(e.target.value)} placeholder="name@rapidsos.com" className="mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">
          Level
          <select value={level} onChange={(e) => setLevel(e.target.value)} className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            {integration.levels.map((l) => (
              <option key={l.id} value={l.id}>
                {l.id}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-muted-foreground">
          Until (optional)
          <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="mt-1" />
        </label>
        <label className="text-xs text-muted-foreground">
          Reason
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this person needs it" className="mt-1" />
        </label>
        <div className="md:col-span-2">
          {error ? <div className="mb-3"><Problem message={error} /></div> : null}
          <Button type="submit" disabled={!ready}>
            {busy ? 'Giving…' : 'Give'}
          </Button>
        </div>
      </form>
    </AdminSection>
  );
}
